// TECH DEBT: typing is hard for this need time to fix
/* eslint-disable @typescript-eslint/no-explicit-any */

import { cn, pluralize } from '@/lib/utils';
import type {
  DeliveryPreference,
  DeliveryStage,
  FinanceManager,
  Lender,
  Priority,
  Status,
} from '@/models/types';
import { Separator } from '@radix-ui/react-separator';
import { keys } from 'lodash';
import { X } from 'lucide-react';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FilterState } from '../types';

type BaseFilterChipProps<F extends FilterState> = {
  filtersState: F;
  setFiltersState: Dispatch<SetStateAction<F>>;
  deleteFilter: () => void;

  //
  // comboBoxOnChange={(value) => {
  //   setFiltersState((prev) => ({
  //     ...prev,
  //     selectedFilters: {
  //       ...prev.selectedFilters,
  //       [value as T]: !(prev.selectedFilters as any)[value as T],
  //     },
  //   }));
  // }}
  // comboBoxOpen={comboBoxIsOpen}
  // // TODO: might need to fix `onOpenChange`
  // onOpenChange={setComboBoxIsOpen}
  // filters={selectedFilters as any}
  // stayOpenAfterSelection={true}
  // align={"start"}
  // popoverContentClassName={cn({
  //   // DEV NOTE: THIS IS A BRITTLE HACK
  //   // but aligning this is hard
  //   "ml-5": negatedCopyBeforeOpen == "is",
  //   "ml-10": negatedCopyBeforeOpen == "is not",
  //   "ml-14": negatedCopyBeforeOpen == "is any of",
  // })}
  FiltersComboBoxPopover: React.ComponentType<{
    comboBoxOnChange: (value: string) => void;
    comboBoxOpen: boolean;
    onOpenChange: (open: boolean) => void;
    filters: any;
    stayOpenAfterSelection: boolean;
    align: 'start' | 'end';
    popoverContentClassName: string;
    children: React.ReactNode;
  }>;
  representativeIcon: React.ReactNode;
  filterName: string;
  filterQuantifier: { singular: string; plural: string };
  SelectedFilterBadge: React.ComponentType<{
    value: any;
    hideCopy: boolean;
  }>;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
  showBadgeOnlyWhenOneIsSelected?: boolean;
  hideFilterComboBoxPopover?: boolean;
  getLabelForValue?: (value: any) => string | undefined;
};

const BaseFilterChip = <
  T extends
    | Status
    | DeliveryStage
    | DeliveryPreference
    | Priority
    | FinanceManager['id']
    | Lender['id']
    | 'price_locked'
    | 'delivery_queued',
  F extends FilterState,
>({
  filtersState,
  setFiltersState,
  deleteFilter,
  FiltersComboBoxPopover,
  hideFilterComboBoxPopover = false,
  representativeIcon,
  filterName,
  filterQuantifier,
  SelectedFilterBadge,
  shouldDeleteWhenNoFiltersAreSelected,
  showBadgeOnlyWhenOneIsSelected = false,
  getLabelForValue,
}: BaseFilterChipProps<F>) => {
  const {
    selectedFilters: selectedFilters,
    filterMetaState: { isNegated },
  } = filtersState;

  // TECH DEBT: gotta DRY this logic
  const filtersSelected = useMemo(() => {
    return keys(selectedFilters).filter(
      // TODO: fix this type
      (value) => (selectedFilters as any)[value as T],
    );
  }, [selectedFilters]);

  const numberOfStatusFiltersSelected = useMemo(() => {
    return filtersSelected.length;
  }, [filtersSelected]);

  const atLeastOneFilterWasSelected = useMemo(() => {
    return numberOfStatusFiltersSelected > 0;
  }, [numberOfStatusFiltersSelected]);

  const aSelectedFilter = keys(selectedFilters).find(
    // TODO: fix this type
    (filter) => (selectedFilters as any)[filter as T],
  );

  const aSelectedFilterHumanReadable =
    aSelectedFilter && getLabelForValue
      ? getLabelForValue(aSelectedFilter as T)
      : undefined;

  const negatedCopy = useMemo(() => {
    return !isNegated
      ? pluralize(numberOfStatusFiltersSelected, 'is', 'is any of')
      : pluralize(numberOfStatusFiltersSelected, 'is not', 'is not');
  }, [isNegated, numberOfStatusFiltersSelected]);

  const [comboBoxIsOpen, setComboBoxIsOpen] = useState(false);

  // UX HACK: we'r using the `negatedCopyBeforeOpen` to measure ml-x in `popoverContentClassName`
  // so that the popover can be aligned, but also stable as things shift as statuses are selected
  const [negatedCopyBeforeOpen, setNegatedCopyBeforeOpen] =
    useState(negatedCopy);
  useEffect(() => {
    if (comboBoxIsOpen) {
      setNegatedCopyBeforeOpen(negatedCopy);
    }
  }, [comboBoxIsOpen]);

  const atLeastOneFilterWasSelectedRef = useRef(atLeastOneFilterWasSelected);
  useEffect(() => {
    // UX NOTE: once a chip has no filters at all just delet the filter
    if (
      atLeastOneFilterWasSelectedRef.current &&
      !atLeastOneFilterWasSelected &&
      shouldDeleteWhenNoFiltersAreSelected
    ) {
      deleteFilter();
    }
    atLeastOneFilterWasSelectedRef.current = atLeastOneFilterWasSelected;
  }, [atLeastOneFilterWasSelected]);

  return (
    <div>
      {atLeastOneFilterWasSelected && (
        <div className="flex flex-row w-fit bg-white text-gray-500 gap-2 h-fit border rounded-md p-0 items-center">
          <div className="flex flex-row gap-1 items-center">
            {representativeIcon}
            <span className="flex flex-row items-center text-nowrap">
              {filterName}{' '}
            </span>
            <Separator
              orientation="vertical"
              className="testing-sep w-[1px] h-[24px]  bg-gray-300"
            />

            <FiltersComboBoxPopover
              comboBoxOnChange={(value) => {
                setFiltersState((prev) => ({
                  ...prev,
                  selectedFilters: {
                    ...(prev.selectedFilters as any),
                    [value as T]: !(prev.selectedFilters as any)[value as T],
                  },
                }));
              }}
              comboBoxOpen={comboBoxIsOpen}
              // TODO: might need to fix `onOpenChange`
              onOpenChange={setComboBoxIsOpen}
              filters={selectedFilters as any}
              stayOpenAfterSelection={true}
              align={'start'}
              popoverContentClassName={cn({
                // DEV NOTE: THIS IS A BRITTLE HACK
                // but aligning this is hard
                'ml-5': negatedCopyBeforeOpen == 'is',
                'ml-10': negatedCopyBeforeOpen == 'is not',
                'ml-14': negatedCopyBeforeOpen == 'is any of',
              })}
              // side
            >
              <div
                className="cursor-pointer text-nowrap"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFiltersState((prev) => {
                    const currentIsNegated = prev.filterMetaState.isNegated;

                    return {
                      ...prev,
                      filterMetaState: {
                        ...prev.filterMetaState,
                        isNegated: !currentIsNegated,
                      },
                    };
                  });
                }}
              >
                {negatedCopy}
              </div>
            </FiltersComboBoxPopover>

            <Separator
              orientation="vertical"
              className="testing-sep w-[1px] h-[24px] bg-gray-300"
            />

            <div
              className={cn('flex flex-row', {
                'cursor-pointer': !hideFilterComboBoxPopover,
              })}
              onClick={() => setComboBoxIsOpen(true)}
            >
              {(!showBadgeOnlyWhenOneIsSelected ||
                filtersSelected.length == 1) && (
                <div
                  className="flex flex-row relative h-5"
                  style={{
                    width: 12 + (14 * (3 / 4) * filtersSelected.length - 1),
                  }}
                >
                  {filtersSelected.map((filter, index) => (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${(index * 14 * 3) / 4}px`,
                        zIndex: index,
                        top: 0,
                        // background: "white",
                      }}
                      className="pointer-events-none"
                      key={`badge-for-${filterName}-${filter}`}
                    >
                      <SelectedFilterBadge value={filter as any} hideCopy />
                    </div>
                  ))}
                </div>
              )}

              <span className="text-nowrap">
                {/* TODO: make this pluralize correctly */}
                {pluralize(
                  numberOfStatusFiltersSelected,
                  (aSelectedFilterHumanReadable as string) ||
                    filterQuantifier.singular,
                  `${numberOfStatusFiltersSelected} ${filterQuantifier.plural}`,
                )}
              </span>
            </div>
          </div>

          <X
            className="size-4 cursor-pointer border rounded-md mr-1"
            onClick={deleteFilter}
          />
        </div>
      )}
    </div>
  );
};

export default BaseFilterChip;
